import * as React from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { useHydrateAtoms } from 'jotai/utils';
import { BreadcrumbJsonLd } from 'next-seo';

import { useAuth } from '@restworld/feature-authentication';
import { imageVariant, ogDefaultImageUrl } from '@restworld/utils-common';
import { useRestaurantsService } from '@restworld/data-services';

import {
  fetchRestaurantInfoInternal,
  RestaurantStaticType,
  useFetchRestaurantInformation,
} from '../restaurant-showcase/server-side/fetch-restaurant-information';
import { RestaurantNotFound } from '../restaurant-showcase/components/restaurant-not-found';
import { VenueInformation } from './venue-info';
import VenueAboutUS from './venue-about-us';
import VenueValues from './venue-values';
import VenueBenefits from './venue-benefits';
import { LayoutWrapper } from '../../layouts';
import VenueTeams from './venue-teams';
import VenueOwnerMessage from './venue-owner-message';
import VenueJobPositions from './venue-job-positions';
import VenueRoles from './venue-roles';
import VenueLinks from './venue-links';
import VenueFooter from './venue-footer';
import VenueStickyCTA from './venue-cta';
import { useIsRestaurantOwner, useShowCasePreviewCard } from './hooks';
import { buildVenueMeta } from '../../lib/seo/venue-meta';
import { venueOgImage } from '../../lib/seo/venue-og-image';
import { shouldNoindexVenue } from '../../lib/seo/venue-robots';
import { useCaptureUtmSource } from '../restaurant-showcase/server-side/use-capture-utm-source';
import VenuePublicConfirmModal from './venue-public-confirm-modal';
import { restaurantPopupInfoAtom } from '../worker-entry-point';

export const restaurantDetailsAtom = atom<Partial<RestaurantStaticType>>({});
export const restaurantInformationQueryKey = ['restaurant-details'];

export const useFetchRestaurantInformationQuery = () => {
  const router = useRouter();
  const setRestaurantDetails = useSetAtom(restaurantDetailsAtom);
  const setRestaurantPopupInfo = useSetAtom(restaurantPopupInfoAtom);
  const { fetchRestaurantInformation } = useFetchRestaurantInformation();
  const restaurantService = useRestaurantsService();
  const slug = router.query.slug as string;
  const refetchRestaurantDataInCaseFailure = React.useCallback(async () => {
    try {
      const data = await fetchRestaurantInfoInternal(slug, (slug: string) => restaurantService.showRestaurant(slug));
      setRestaurantDetails(data);
    } catch (error) {
      // Restaurant not found or error fetching - set null to show not found UI
      setRestaurantDetails(null);
    }
  }, [restaurantService, setRestaurantDetails, slug]);

  const queryData = useQuery({
    queryKey: restaurantInformationQueryKey,
    queryFn: async () => {
      const data = await fetchRestaurantInformation(slug);
      setRestaurantDetails(data);
      setRestaurantPopupInfo({
        restaurantId: data.restaurantId,
        restaurantEmployerName: data.restaurantEmployerName,
        restaurantOrganizationName: data.restaurantOrganizationName,
        restaurantName: data.restaurantName,
        restaurantAddress: data.mapLocation?.address,
      });
      return data;
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!slug,
    // initialData: restaurantDetails,
    retry: (failureCount, error) => {
      if (error?.['code'] === 404) {
        refetchRestaurantDataInCaseFailure();
        return false; // Do not retry on not found error for employer
      }
      return failureCount < 3; // Retry up to 3 times for other errors
    },
  });

  return {
    isLoading: queryData.isLoading,
    data: queryData.data,
  };
};

export const ShowcaseSectionTitle = ({ title }: { title: string }) => (
  <h5 className="self-stretch justify-start text-neutral-700 text-lg lg:text-[20px] font-semibold">{title}</h5>
);

interface IShowCaseV2ContentProps {
  showCaseInformation?: RestaurantStaticType;
}

const ShowCaseV2Content: React.FunctionComponent<IShowCaseV2ContentProps> = ({ showCaseInformation }) => {
  const restaurantDetails = useAtomValue(restaurantDetailsAtom);
  useCaptureUtmSource({ restaurantId: restaurantDetails?.restaurantId });
  useShowCasePreviewCard({ slug: restaurantDetails?.slug });
  useFetchRestaurantInformationQuery();

  // SSR meta/structured data: use showCaseInformation prop directly (available at build time).
  // restaurantDetails atom is only populated client-side after hydration.
  const { title: metaTitle, description: metaDescription } = buildVenueMeta({
    restaurantName: showCaseInformation?.restaurantName,
    briefDescription: showCaseInformation?.briefDescription,
    metaDescription: showCaseInformation?.metaDescription,
    locality: showCaseInformation?.mapLocation?.locality,
  });
  // The venue's own image when it has one, the brand default when it does not.
  // The old code looked only at `meta_image`, which nothing populates, so 195
  // showcases in 200 had a gallery sitting right there and still emitted no
  // `og:image` at all. See `venueOgImage` for why no width and height are
  // declared alongside it, and for the seam with the backend pipeline that will
  // eventually fill `meta_image` properly.
  //
  // Only a gallery photo is resized, and only here rather than inside the helper,
  // the same way `venue-info` does for the gallery: `lg` asks for the 1600px
  // derivative instead of the archival original, which takes the heaviest first
  // photo in the sample from 3,5 MB down to 1,46 MB. A `meta_image` is passed
  // through untouched because it is already the right image at the right size.
  const venueImage = venueOgImage({
    metaImage: showCaseInformation?.metaImage,
    pictures: showCaseInformation?.pictures,
  });
  const ogImageUrl =
    venueImage?.kind === 'gallery-photo' ? imageVariant(venueImage.url, 'lg') : venueImage?.url ?? ogDefaultImageUrl();
  // Dimensions are declared only for a `meta_image`, where they are known to be
  // true: `ImageComposer` in core-services hardcodes 1200x630 (`@image_width` /
  // `@image_height`), and the one showcase carrying a hand-set value today
  // measures exactly that. For a gallery photo they are deliberately left out,
  // because 30,1% of first photos are portrait and 25,9% are narrower than
  // 1200px, and a scraper that trusts a declared size lays out a card the file
  // does not fill.
  const ogImageSize = venueImage?.kind === 'meta-image' ? { width: 1200, height: 630 } : {};
  const canonicalUrl = showCaseInformation?.slug
    ? `https://www.restworld.it/locale/${showCaseInformation.slug}`
    : undefined;

  const seoProps = {
    title: metaTitle,
    description: metaDescription,
    // A showcase with nothing of its own on it is not worth an index slot, so we
    // stop asking for one. The rule that decides "nothing of its own" is not
    // here: `hasOwnContent` arrives already computed from core-services, and the
    // sitemap reads the same flag from the ids endpoint. See `shouldNoindexVenue`
    // for why the unknown case has to keep indexing. The page re-enters on its
    // own once the venue is filled in, within the 60s `revalidate` of
    // `getStaticProps`.
    noindex: shouldNoindexVenue(showCaseInformation?.hasOwnContent),
    canonical: canonicalUrl,
    openGraph: {
      title: metaTitle,
      description: metaDescription,
      type: 'article',
      url: canonicalUrl,
      images: [{ url: ogImageUrl, alt: metaTitle, ...ogImageSize }],
    },
    // `next-seo` 5.15 builds `twitter:card`, `twitter:site` and
    // `twitter:creator` and nothing else: there is no `twitter.image` option to
    // pass, so the tag has to be written by hand. Without it the page keeps
    // promising `summary_large_image` and keeps handing X nothing of its own.
    additionalMetaTags: [
      { name: 'twitter:image', content: ogImageUrl },
      { name: 'twitter:image:alt', content: metaTitle },
    ],
  };

  const isOwner = useIsRestaurantOwner();
  useHydrateAtoms([
    [restaurantDetailsAtom, showCaseInformation],
    [
      restaurantPopupInfoAtom,
      {
        restaurantId: showCaseInformation?.restaurantId,
        restaurantEmployerName: showCaseInformation?.restaurantEmployerName,
        restaurantOrganizationName: showCaseInformation?.restaurantOrganizationName,
        restaurantName: showCaseInformation?.restaurantName,
        restaurantAddress: showCaseInformation?.mapLocation?.address,
      },
    ],
  ]);
  const { user } = useAuth();

  // If the restaurant is not found, show the not-found page
  if (!showCaseInformation && !restaurantDetails?.restaurantId) {
    return <RestaurantNotFound />;
  }

  // FoodEstablishment is the umbrella type — venue typology on backend is free-text and not safe to map to specific schema.org types.
  const social = showCaseInformation?.socialMediaUrl;
  const sameAs = [
    showCaseInformation?.mapLocation?.googleMapsURL,
    social?.website_url,
    social?.facebook_url,
    social?.instagram_url,
    social?.tripadvisor_url,
    social?.linkedin_url,
    social?.tiktok_url,
    social?.the_fork_url,
  ].filter((u): u is string => Boolean(u));

  const restaurantJsonLd = showCaseInformation?.restaurantName
    ? {
        '@context': 'https://schema.org',
        '@type': 'FoodEstablishment',
        ...(canonicalUrl ? { '@id': `${canonicalUrl}#venue` } : {}),
        name: showCaseInformation.restaurantName,
        ...(showCaseInformation.briefDescription ? { description: showCaseInformation.briefDescription } : {}),
        ...(showCaseInformation.restaurantLogo ? { logo: showCaseInformation.restaurantLogo } : {}),
        ...(showCaseInformation.pictures?.[0] ? { image: showCaseInformation.pictures[0] } : {}),
        ...(canonicalUrl ? { url: canonicalUrl } : {}),
        ...(sameAs.length > 0 ? { sameAs } : {}),
        ...(showCaseInformation.mapLocation?.address
          ? {
              address: {
                '@type': 'PostalAddress',
                streetAddress: showCaseInformation.mapLocation.address,
                ...(showCaseInformation.mapLocation.locality
                  ? { addressLocality: showCaseInformation.mapLocation.locality }
                  : {}),
              },
            }
          : {}),
        ...(showCaseInformation.mapLocation?.lat && showCaseInformation.mapLocation?.lon
          ? {
              geo: {
                '@type': 'GeoCoordinates',
                latitude: showCaseInformation.mapLocation.lat,
                longitude: showCaseInformation.mapLocation.lon,
              },
            }
          : {}),
        ...(showCaseInformation.rating && showCaseInformation.totalReviews
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: showCaseInformation.rating,
                reviewCount: showCaseInformation.totalReviews,
              },
            }
          : {}),
      }
    : null;

  return (
    <div className="relative w-full h-screen flex flex-col justify-between items-center">
      <LayoutWrapper
        seoProps={seoProps}
        // The showcase is written by the venue and stays Italian whatever the
        // interface language is, so /en/locale/<slug> is a duplicate and not a
        // translation: canonical to the Italian version, and no hreflang.
        languageSeoMode="consolidated"
        hasFooter={false}
        hasNavbar={false}
        title="Locale"
        noPaddingTop
        noHorizontalPadding
        noPaddingBottom
      >
        <BreadcrumbJsonLd
          itemListElements={[
            { position: 1, name: 'Home', item: 'https://www.restworld.it' },
            { position: 2, name: 'Ristoranti', item: 'https://www.restworld.it/cerco-lavoro' },
            {
              position: 3,
              name: showCaseInformation?.restaurantName || 'Locale',
              item: canonicalUrl || 'https://www.restworld.it',
            },
          ]}
        />
        {restaurantJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(restaurantJsonLd).replace(/<\//g, '<\\/') }}
          />
        )}
        <div className="px-4 pt-[2.5rem] pb-[6rem] max-w-[640px] mx-auto">
          <div className="flex flex-col justify-start items-start gap-[80px] ">
            <VenueInformation />
            <VenueAboutUS />
            <VenueBenefits />
            <VenueValues />
            <VenueTeams />
            <VenueOwnerMessage />
            <VenueJobPositions />
            <VenueRoles />
            <VenueLinks />
          </div>
        </div>
      </LayoutWrapper>
      <VenueFooter venueLocation={showCaseInformation?.mapLocation} />
      {(!isOwner || !user || user?.type === 'worker') && <VenueStickyCTA />}
      <VenuePublicConfirmModal />
    </div>
  );
};

export default ShowCaseV2Content;
